import React, { useState, useMemo, useRef } from 'react';
import { Search, ArrowRightLeft, Upload, FileText, Download, X, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import api from '../../lib/api';
import { toast } from 'sonner';

export const OrgMembersPanel = ({ orgId, organizations, users, isSuperAdmin = false, onMembersChanged }) => {
  const orgMembers = users.filter(u =>
    u.roles?.some(r => r.organization_id === orgId && r.is_active)
  );
  const orgName = organizations.find(o => o.id === orgId)?.name || 'Organization';
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [moveDialog, setMoveDialog] = useState(false);
  const [moveForm, setMoveForm] = useState({
    target_organization_id: '',
    default_role: 'user',
    deactivate_previous_org_roles: true,
  });
  const [moving, setMoving] = useState(false);

  // --- CSV Upload state ---
  const [csvDialog, setCsvDialog] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvPreview, setCsvPreview] = useState(null); // dry-run result
  const [csvDeactivate, setCsvDeactivate] = useState(true);
  const [csvSubmitting, setCsvSubmitting] = useState(false);
  const csvInputRef = useRef(null);

  const filtered = useMemo(() =>
    orgMembers.filter(u =>
      !search ||
      (u.name || '').toLowerCase().includes(search.toLowerCase()) ||
      (u.email || '').toLowerCase().includes(search.toLowerCase())
    ),
    [orgMembers, search]
  );

  const getRoleBadge = (user) => {
    const role = user.roles?.find(r => r.organization_id === orgId)?.role;
    if (role === 'super_admin') return <Badge className="bg-purple-100 text-purple-700 text-xs">Super Admin</Badge>;
    if (role === 'org_admin') return <Badge className="bg-blue-100 text-blue-700 text-xs">Org Admin</Badge>;
    return <Badge className="bg-gray-100 text-gray-600 text-xs">User</Badge>;
  };

  const toggle = (id) => setSelectedIds(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const clearSelection = () => setSelectedIds([]);

  const openMoveDialog = () => {
    setMoveForm({
      target_organization_id: '',
      default_role: 'user',
      deactivate_previous_org_roles: true,
    });
    setMoveDialog(true);
  };

  const runBulkMove = async () => {
    if (!selectedIds.length || !moveForm.target_organization_id) return;
    if (moveForm.target_organization_id === orgId) {
      toast.error('Target organisation must differ from the current one.');
      return;
    }
    setMoving(true);
    try {
      const res = await api.post('/rbac/users/bulk-move', {
        user_ids: selectedIds,
        target_organization_id: moveForm.target_organization_id,
        default_role: moveForm.default_role,
        deactivate_previous_org_roles: moveForm.deactivate_previous_org_roles,
      });
      toast.success(res.data.summary || `Moved ${res.data.moved?.length || 0} user(s)`);
      if (res.data.already_in_target?.length) {
        toast.info(`${res.data.already_in_target.length} skipped (already in target).`);
      }
      clearSelection();
      setMoveDialog(false);
      onMembersChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'Move failed');
    } finally {
      setMoving(false);
    }
  };

  // --- CSV Upload handlers ---
  const resetCsvState = () => {
    setCsvFile(null);
    setCsvPreview(null);
    setCsvDeactivate(true);
    if (csvInputRef.current) csvInputRef.current.value = '';
  };

  const openCsvDialog = () => {
    resetCsvState();
    setCsvDialog(true);
  };

  const handleCsvFile = async (file) => {
    if (!file) return;
    const isCsv =
      file.type === 'text/csv' ||
      file.name.toLowerCase().endsWith('.csv');
    if (!isCsv) {
      toast.error('Please upload a .csv file');
      return;
    }
    setCsvFile(file);
    setCsvPreview(null);
    // Trigger a dry-run validation automatically
    setCsvSubmitting(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await api.post(
        '/rbac/users/bulk-move/csv?dry_run=true',
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setCsvPreview(res.data);
      if (res.data?.error_count) {
        toast.warning(`${res.data.error_count} row(s) have errors. Review below before applying.`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'CSV validation failed');
      setCsvFile(null);
      if (csvInputRef.current) csvInputRef.current.value = '';
    } finally {
      setCsvSubmitting(false);
    }
  };

  const applyCsvMove = async () => {
    if (!csvFile) return;
    setCsvSubmitting(true);
    try {
      const form = new FormData();
      form.append('file', csvFile);
      const res = await api.post(
        `/rbac/users/bulk-move/csv?deactivate_previous_org_roles=${csvDeactivate}`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(res.data.summary || 'CSV processed');
      onMembersChanged?.();
      setCsvDialog(false);
      resetCsvState();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'CSV upload failed');
    } finally {
      setCsvSubmitting(false);
    }
  };

  const downloadCsvTemplate = () => {
    const csv =
      'email,target_org,role\n' +
      'alice@example.com,Acme Corp,user\n' +
      'bob@example.com,Acme Corp,org_admin\n';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bulk-move-users-template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Card data-testid="org-members-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base">{orgName} Members</CardTitle>
            <CardDescription>{orgMembers.length} member{orgMembers.length !== 1 ? 's' : ''}</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {isSuperAdmin && (
              <Button
                size="sm"
                variant="outline"
                onClick={openCsvDialog}
                data-testid="org-members-csv-upload-btn"
                title="Bulk-move users from a CSV file (email,target_org,role)"
              >
                <Upload className="h-3.5 w-3.5 mr-1.5" />
                CSV Upload
              </Button>
            )}
            {isSuperAdmin && selectedIds.length > 0 && (
              <>
                <span className="text-xs text-slate-600 font-medium" data-testid="org-members-selected-count">
                  {selectedIds.length} selected
                </span>
                <Button
                  size="sm"
                  onClick={openMoveDialog}
                  data-testid="org-members-bulk-move-btn"
                >
                  <ArrowRightLeft className="h-3.5 w-3.5 mr-1.5" />
                  Move to org…
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={clearSelection}
                  data-testid="org-members-clear-selection-btn"
                >
                  Clear
                </Button>
              </>
            )}
            <div className="relative w-56">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search members..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-9 text-sm"
                data-testid="org-members-search"
              />
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {filtered.length === 0 ? (
          <div className="text-center py-8 text-gray-500 text-sm">
            {search ? 'No members match your search.' : 'No members in this organization yet.'}
          </div>
        ) : (
          <div className="divide-y">
            {filtered.map(user => (
              <div
                key={user.user_id}
                className={`flex items-center justify-between py-3 px-1 ${
                  selectedIds.includes(user.user_id) ? 'bg-teal-50' : ''
                }`}
                data-testid={`org-member-${user.user_id}`}
              >
                <div className="flex items-center gap-3">
                  {isSuperAdmin && (
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(user.user_id)}
                      onChange={() => toggle(user.user_id)}
                      className="h-4 w-4 accent-teal-600 cursor-pointer"
                      aria-label={`Select ${user.name || user.email}`}
                      data-testid={`org-member-select-${user.user_id}`}
                    />
                  )}
                  <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-sm font-medium text-slate-600">
                    {(user.name || user.email || '?')[0].toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-800">{user.name || 'Unnamed'}</p>
                    <p className="text-xs text-slate-500">{user.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {getRoleBadge(user)}
                  <Badge variant={user.is_active !== false ? 'outline' : 'destructive'} className="text-xs">
                    {user.is_active !== false ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={moveDialog} onOpenChange={(o) => !o && setMoveDialog(false)}>
        <DialogContent data-testid="org-members-bulk-move-dialog">
          <DialogHeader>
            <DialogTitle>Move {selectedIds.length} user{selectedIds.length !== 1 ? 's' : ''}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Target organisation</Label>
              <Select
                value={moveForm.target_organization_id}
                onValueChange={(v) => setMoveForm(f => ({ ...f, target_organization_id: v }))}
              >
                <SelectTrigger data-testid="bulk-move-users-org-selector">
                  <SelectValue placeholder="Select organisation..." />
                </SelectTrigger>
                <SelectContent>
                  {organizations.filter(o => o.id !== orgId).map(org => (
                    <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Default role in target (if not already a member)</Label>
              <Select
                value={moveForm.default_role}
                onValueChange={(v) => setMoveForm(f => ({ ...f, default_role: v }))}
              >
                <SelectTrigger data-testid="bulk-move-users-role-selector">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="org_admin">Org Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-start gap-2 rounded-md border border-slate-200 p-3 bg-slate-50">
              <input
                id="deactivate-previous-roles"
                type="checkbox"
                checked={moveForm.deactivate_previous_org_roles}
                onChange={(e) =>
                  setMoveForm(f => ({ ...f, deactivate_previous_org_roles: e.target.checked }))
                }
                className="mt-0.5 h-4 w-4 accent-teal-600"
                data-testid="bulk-move-users-deactivate-toggle"
              />
              <label htmlFor="deactivate-previous-roles" className="text-sm text-slate-700 cursor-pointer">
                <strong>Deactivate their existing org roles</strong>
                <div className="text-xs text-slate-500 mt-0.5">
                  Revokes prior org-scoped role rows (super admin rows are left untouched).
                  Leave off if you want the users to remain in multiple orgs.
                </div>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveDialog(false)} disabled={moving}>
              Cancel
            </Button>
            <Button
              onClick={runBulkMove}
              disabled={moving || !moveForm.target_organization_id}
              data-testid="bulk-move-users-confirm-btn"
            >
              {moving ? 'Moving…' : 'Move'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV Upload Dialog */}
      <Dialog
        open={csvDialog}
        onOpenChange={(o) => { if (!o) { setCsvDialog(false); resetCsvState(); } }}
      >
        <DialogContent className="max-w-2xl" data-testid="org-members-csv-upload-dialog">
          <DialogHeader>
            <DialogTitle>Bulk-move users from CSV</DialogTitle>
            <DialogDescription>
              Upload a spreadsheet (<code>email,target_org,role</code>) to reassign many
              users in one pass. We'll validate every row first and show you the preview
              before anything is applied.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Template + file picker */}
            <div className="flex items-center justify-between gap-3 rounded-md border border-slate-200 bg-slate-50 p-3">
              <div className="text-xs text-slate-600">
                Need a starting point?
                <Button
                  variant="link"
                  size="sm"
                  className="h-auto p-0 ml-1 text-xs"
                  onClick={downloadCsvTemplate}
                  data-testid="csv-template-download-btn"
                >
                  <Download className="h-3 w-3 mr-1 inline" />
                  Download template
                </Button>
              </div>
              <div className="text-xs text-slate-500">
                Max 2000 rows · UTF-8 · headers required
              </div>
            </div>

            {!csvFile && (
              <label
                className="flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-slate-300 bg-white p-8 cursor-pointer hover:border-teal-400 hover:bg-teal-50 transition-colors"
                data-testid="csv-dropzone"
              >
                <Upload className="h-8 w-8 text-slate-400" />
                <div className="text-sm font-medium text-slate-700">
                  Click to choose a CSV file
                </div>
                <div className="text-xs text-slate-500">
                  Headers required: email, target_org, role
                </div>
                <input
                  ref={csvInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={(e) => handleCsvFile(e.target.files?.[0])}
                  data-testid="csv-file-input"
                />
              </label>
            )}

            {csvFile && (
              <div className="flex items-center justify-between rounded-md border border-slate-200 bg-white p-3">
                <div className="flex items-center gap-2 min-w-0">
                  <FileText className="h-5 w-5 text-teal-600 flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">
                      {csvFile.name}
                    </div>
                    <div className="text-xs text-slate-500">
                      {(csvFile.size / 1024).toFixed(1)} KB
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={resetCsvState}
                  disabled={csvSubmitting}
                  data-testid="csv-clear-btn"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}

            {csvSubmitting && !csvPreview && (
              <div className="text-xs text-slate-500 text-center">Validating rows…</div>
            )}

            {csvPreview && (
              <div className="space-y-3" data-testid="csv-preview-block">
                <div className="grid grid-cols-3 gap-2">
                  <div className="rounded-md bg-slate-50 border border-slate-200 p-2 text-center">
                    <div className="text-xs text-slate-500">Total rows</div>
                    <div className="text-lg font-semibold text-slate-800">
                      {csvPreview.total_rows}
                    </div>
                  </div>
                  <div className="rounded-md bg-emerald-50 border border-emerald-200 p-2 text-center">
                    <div className="text-xs text-emerald-700 flex items-center justify-center gap-1">
                      <CheckCircle2 className="h-3 w-3" /> Valid
                    </div>
                    <div className="text-lg font-semibold text-emerald-700"
                         data-testid="csv-valid-count">
                      {csvPreview.valid_count}
                    </div>
                  </div>
                  <div className={`rounded-md border p-2 text-center ${
                    csvPreview.error_count
                      ? 'bg-amber-50 border-amber-200'
                      : 'bg-slate-50 border-slate-200'
                  }`}>
                    <div className="text-xs text-amber-700 flex items-center justify-center gap-1">
                      <AlertTriangle className="h-3 w-3" /> Errors
                    </div>
                    <div className="text-lg font-semibold text-amber-700"
                         data-testid="csv-error-count">
                      {csvPreview.error_count}
                    </div>
                  </div>
                </div>

                {csvPreview.errors?.length > 0 && (
                  <div className="rounded-md border border-amber-200 bg-amber-50 p-3 max-h-40 overflow-y-auto"
                       data-testid="csv-errors-list">
                    <div className="text-xs font-semibold text-amber-800 mb-1">
                      Row errors ({csvPreview.errors.length} shown)
                    </div>
                    <ul className="text-xs text-amber-900 space-y-0.5">
                      {csvPreview.errors.slice(0, 20).map((e, i) => (
                        <li key={`${e.row}-${e.email || 'noemail'}-${i}`}>
                          Row {e.row}{e.email ? ` (${e.email})` : ''}: {e.reason}
                        </li>
                      ))}
                      {csvPreview.errors.length > 20 && (
                        <li className="italic">…and {csvPreview.errors.length - 20} more</li>
                      )}
                    </ul>
                  </div>
                )}

                {csvPreview.preview?.length > 0 && (
                  <div className="rounded-md border border-slate-200 bg-white max-h-48 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr className="text-left text-slate-500">
                          <th className="px-2 py-1.5">Row</th>
                          <th className="px-2 py-1.5">Email</th>
                          <th className="px-2 py-1.5">Target org</th>
                          <th className="px-2 py-1.5">Role</th>
                        </tr>
                      </thead>
                      <tbody>
                        {csvPreview.preview.slice(0, 25).map((r) => (
                          <tr key={r.row} className="border-t border-slate-100">
                            <td className="px-2 py-1.5 text-slate-500">{r.row}</td>
                            <td className="px-2 py-1.5 text-slate-800">{r.email}</td>
                            <td className="px-2 py-1.5 text-slate-700">{r.target_org_name}</td>
                            <td className="px-2 py-1.5">
                              <Badge variant="outline" className="text-xs">{r.role}</Badge>
                            </td>
                          </tr>
                        ))}
                        {csvPreview.preview.length > 25 && (
                          <tr>
                            <td colSpan={4} className="px-2 py-1.5 text-center text-slate-400 italic">
                              …and {csvPreview.preview.length - 25} more valid row(s)
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="flex items-start gap-2 rounded-md border border-slate-200 p-3 bg-slate-50">
                  <input
                    id="csv-deactivate-previous"
                    type="checkbox"
                    checked={csvDeactivate}
                    onChange={(e) => setCsvDeactivate(e.target.checked)}
                    className="mt-0.5 h-4 w-4 accent-teal-600"
                    data-testid="csv-deactivate-toggle"
                  />
                  <label htmlFor="csv-deactivate-previous" className="text-sm text-slate-700 cursor-pointer">
                    <strong>Deactivate each user's existing org roles</strong>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Revokes prior org-scoped role rows (super admin rows are left untouched).
                    </div>
                  </label>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setCsvDialog(false); resetCsvState(); }}
              disabled={csvSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={applyCsvMove}
              disabled={csvSubmitting || !csvPreview || csvPreview.valid_count === 0}
              data-testid="csv-apply-btn"
            >
              {csvSubmitting
                ? 'Applying…'
                : csvPreview
                  ? `Apply ${csvPreview.valid_count} move(s)`
                  : 'Apply'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
